/**
 * Preview-only mock currency rates.
 *
 * Consumed by `useCurrencyRates` ONLY when:
 *   1. `TICKER_PREVIEW_MOCK_ENABLED === true` (feature flag), AND
 *   2. The real batch endpoint returned an error.
 *
 * Rates are deliberately approximate and CLEARLY LABELED as preview
 * data in the UI ("Preview rates" badge). The label ensures no one —
 * least of all the founder testing the UI — mistakes these for live
 * market rates.
 *
 * Base of these rates: USD as of Q3 2026. Other bases are derived
 * naively (rate[X→Y] = rate[USD→Y] / rate[USD→X]) which is enough
 * for a UI smoke test but not for arbitrage.
 */

// USD → * rates (approximate mid-market snapshot; refresh occasionally)
const USD_MOCK = {
  EUR: 0.87, JPY: 161.5, GBP: 0.74, AUD: 1.43, CAD: 1.40,
  MXN: 18.20, PHP: 56.30, THB: 34.20, KRW: 1370.0, VND: 25200.0,
  CNY: 7.19, INR: 83.10, CHF: 0.87, SEK: 10.30, NOK: 10.70,
  DKK: 6.52, SGD: 1.33, HKD: 7.81, TWD: 32.10, IDR: 15700.0,
  MYR: 4.42, AED: 3.67, SAR: 3.75, TRY: 32.90, BRL: 5.15,
  ARS: 950.0, COP: 4080.0, PEN: 3.75, CLP: 940.0, PLN: 3.86,
  CZK: 22.10, HUF: 353.0, ZAR: 18.30, ILS: 3.68, EGP: 47.90,
  USD: 1.0,
};

/**
 * Derive rates for a given base against the requested symbols.
 * Uses the USD_MOCK map as the pivot.
 */
export function getPreviewRates(base, symbols) {
  const b = (base || "USD").toUpperCase();
  const baseUsdRate = USD_MOCK[b];
  if (!baseUsdRate) return {}; // Unknown base — no preview data.
  const out = {};
  for (const raw of symbols) {
    const s = raw.toUpperCase();
    if (s === b) continue;
    const targetUsdRate = USD_MOCK[s];
    if (!targetUsdRate) continue;
    // rate(base → target) = USD→target / USD→base
    const rate = targetUsdRate / baseUsdRate;
    // Round for display sanity (avoid 15-digit float noise in the UI).
    out[s] = rate >= 100 ? Number(rate.toFixed(2)) : Number(rate.toFixed(5));
  }
  return out;
}

export const PREVIEW_MOCK_LABEL = "preview";
